import { combineReducers, Reducer } from 'redux';
import { CLEAR_PRIO_CACHE } from '../../../actions';
import { DriveItemId, ProjectId } from '../../../models/Types';
import {
  CLOSE_WIDGET_AREA,
  OPEN_WIDGET_AREA,
  SELECT_WIDGET,
  SET_BUDDY_ICON_VISIBILITY,
  SET_DOCUMENTSWIDGET_ACTIVE_DRIVE_ITEM,
  SET_DOCUMENTSWIDGET_ACTIVE_PROJECT,
  SET_NEW_TASK_CREATED,
  SET_WIDGETBAR_ACTIVE_PROJECT,
  WidgetAreaDisplayAction,
} from '../actions';

export type Widget =
  | 'mail'
  | 'documents'
  | 'planner'
  | 'uploadManager'
  | 'comment'
  | 'prioBuddy'
  | 'accounting'
  | 'task'
  | 'prioShare';

export interface WidgetAreaReducerState {
  displayState: WidgetAreaDisplayState;
  activeProjectId: ProjectId;
  documentsWidget: DocumentsWidgetState;
  buddyIcon: BuddyIconState;
  newTaskCreated: NewTaskCreatedState;
}

export interface WidgetAreaDisplayState {
  open: boolean;
  selectedWidget: Widget;
}

const initialState: WidgetAreaDisplayState = {
  open: false,
  selectedWidget: 'documents',
};

const displayState: Reducer<WidgetAreaDisplayState, WidgetAreaDisplayAction> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case OPEN_WIDGET_AREA: {
      const { selectedWidget } = action;
      return {
        open: true,
        selectedWidget: selectedWidget ?? state.selectedWidget,
      };
    }
    case CLOSE_WIDGET_AREA: {
      const { selectedWidget } = action;
      return {
        open: false,
        selectedWidget: selectedWidget ?? state.selectedWidget,
      };
    }
    case SELECT_WIDGET: {
      const { selectedWidget } = action;
      return {
        ...state,
        selectedWidget,
      };
    }
    case CLEAR_PRIO_CACHE: {
      return initialState;
    }
    default:
      return state;
  }
};

const activeProjectId: Reducer<ProjectId, any> = (state = null, action) => {
  switch (action.type) {
    case SET_WIDGETBAR_ACTIVE_PROJECT: {
      return action.projectId;
    }
    case CLEAR_PRIO_CACHE: {
      return null;
    }
    default:
      return state;
  }
};

interface DocumentsWidgetState {
  activeProjectId?: ProjectId;
  activeDriveItemId?: DriveItemId;
}

const initialDocumentsWidgetState = {
  activeProjectId: null,
  activeDriveItemId: null,
};

const documentsWidget: Reducer<DocumentsWidgetState, any> = (
  state = initialDocumentsWidgetState,
  action
) => {
  switch (action.type) {
    case SET_DOCUMENTSWIDGET_ACTIVE_DRIVE_ITEM: {
      return {
        ...state,
        activeDriveItemId: action.driveItemId,
      };
    }
    case SET_DOCUMENTSWIDGET_ACTIVE_PROJECT: {
      return {
        ...state,
        activeProjectId: action.projectId,
      };
    }
    case CLEAR_PRIO_CACHE: {
      return initialDocumentsWidgetState;
    }
    default:
      return state;
  }
};

interface BuddyIconState {
  isVisible: boolean;
}

const initialBuddyIconState = {
  isVisible: false,
};

const buddyIcon: Reducer<BuddyIconState, any> = (
  state = initialBuddyIconState,
  action
) => {
  switch (action.type) {
    case SET_BUDDY_ICON_VISIBILITY: {
      return {
        isVisible: action.isVisible,
      };
    }
    case CLEAR_PRIO_CACHE: {
      return initialBuddyIconState;
    }
    default:
      return state;
  }
};

interface NewTaskCreatedState {
  isNewTaskCreated: boolean;
}
const newTaskCreatedInitialState = {
  isNewTaskCreated: false,
};

const newTaskCreated: Reducer<NewTaskCreatedState, any> = (
  state = newTaskCreatedInitialState,
  action
) => {
  switch (action.type) {
    case SET_NEW_TASK_CREATED: {
      return {
        isNewTaskCreated: action.isNewTaskCreated,
      };
    }
    case CLEAR_PRIO_CACHE: {
      return newTaskCreatedInitialState;
    }
    default:
      return state;
  }
};

export default combineReducers<WidgetAreaReducerState>({
  displayState,
  activeProjectId,
  documentsWidget,
  buddyIcon,
  newTaskCreated,
});

export const getDisplayState: (
  state: WidgetAreaReducerState
) => WidgetAreaDisplayState = (state) => state.displayState;

export const getActiveProjectId: (
  state: WidgetAreaReducerState
) => ProjectId = (state) => state.activeProjectId;

export const getDocumentsWidgetActiveProjectId: (
  state: WidgetAreaReducerState
) => ProjectId = (state) => state.documentsWidget.activeProjectId;

export const getBuddyIconVisibility: (
  state: WidgetAreaReducerState
) => boolean = (state) => state.buddyIcon.isVisible;

export const getNewTaskCreated: (state: WidgetAreaReducerState) => boolean = (
  state
) => state.newTaskCreated.isNewTaskCreated;

export const getDocumentsWidgetActiveDriveItemId: (
  state: WidgetAreaReducerState
) => DriveItemId = (state) => state.documentsWidget.activeDriveItemId;
